import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import isEqual from "lodash/isEqual";
import InputText from "shared/components/inputs/InputText";
import InputSelect from "shared/components/inputs/InputSelect";
import Checkbox from "../../components/Checkbox/index.jsx";
import { withTranslation } from "react-i18next";

const itemTypeOptions = [
    { value: "CHECKBOX", label: "Checkbox" },
    { value: "YES_NO", label: "Yes / No" },
    { value: "TEXT_FIELD", label: "Text field" },
    { value: "DATE_TIME", label: "Date / Time" },
    { value: "TEXTAREA", label: "Textarea" }
];

const ChecklistItem = ({
    index,
    add,
    handleOnChange,
    addItem,
    removeItem,
    item,
    checkbox,
    t,
    dataTestId,
    onDragStart,
    onDragOver,
    onDrop,
    readOnly
}) => {
    const [label, setLabel] = useState("");
    const [error, setError] = useState("");
    const [type, setType] = useState("CHECKBOX");
    const [isDragging, setIsDragging] = useState(false);

    useEffect(() => {
        if (item) {
            const { label: nextLabel, error: nextError, type: nextType } = item;
            setLabel(nextLabel || "");
            setError(nextError || "");
            setType(nextType || "CHECKBOX");
        }
    }, [item]);

    const onChangeLabel = (event) => {
        if (readOnly) return;
        const { value } = event.target;
        setLabel(value);
        handleOnChange(index, { label: value, type });
    };

    const onChangeType = (event) => {
        if (readOnly) return;
        const { value } = event.target;
        setType(value);
        handleOnChange(index, { label, type: value });
    };

    const onKeyDown = (event) => {
        if (readOnly) return;
        if (isEqual(event.key, "Enter")) {
            event.preventDefault();
            event.stopPropagation();
            addItem();
        }
    };

    const autoFocus = add && !isEqual(index, 0);

    return (
        <div
            key={index}
            className={`columns is-mobile${isDragging ? " is-dragging" : ""}`}
            draggable={!readOnly}
            onDragStart={(event) => {
                if (!readOnly) {
                    setIsDragging(true);
                    onDragStart && onDragStart(event, index);
                }
            }}
            onDragEnd={() => setIsDragging(false)}
            onDragOver={(event) => onDragOver && onDragOver(event)}
            onDrop={(event) => onDrop && onDrop(event, index)}
        >
            {checkbox && (
                <div className="column is-1-desktop">
                    <Checkbox
                        index={index}
                        checked={false}
                        handleOnChange={onChangeLabel}
                    />
                </div>
            )}
            <div className="column is-11">
                <div className="columns is-mobile" style={
                    { marginRight: "-24px" }
                }>
                    <div className="column is-narrow">
                        <a
                            className="button"
                            data-testid={`reorderChecklistItem${index}`}
                        >
                            <span className="icon">
                                <i className="fas fa-arrows-alt" />
                            </span>
                        </a>
                    </div>
                    <div className="column is-3">
                        <InputSelect
                            name="type"
                            value={type}
                            onChange={onChangeType}
                            items={itemTypeOptions}
                            label="measure.question.type"
                            testId={`checklistItemType${index}`}
                            disabled={readOnly}
                        />
                    </div>
                    <div className="column is-8">
                        <InputText
                            dataTestId={dataTestId}
                            name="label"
                            type="text"
                            autoFocus={autoFocus}
                            label={t("measure.question.label")}
                            onChange={onChangeLabel}
                            onKeyDown={onKeyDown}
                            value={label}
                            error={error ? { message: error, type: "manual" } : undefined}
                            disabled={readOnly}
                        />
                    </div>
                </div>
            </div>
            <div className="column is-1">
                {!readOnly ? (
                    add ? (
                        <a className="button"
                            data-testid={`addChecklistItem${index}`}
                            onClick={() => addItem(index)}>
                            <span className="icon has-text-success">
                                <i className="fas fa-plus-circle" />
                            </span>
                        </a>
                    ) : (
                        <a className="button"
                            data-testid={`removeChecklistItem${index}`}
                            onClick={() => removeItem(index)}>
                            <span className="icon has-text-black">
                                <i className="fas fa-minus-circle" />
                            </span>
                        </a>
                    )
                ) : null}
            </div>
        </div>
    );
};

ChecklistItem.propTypes = {
    index: PropTypes.number.isRequired,
    add: PropTypes.bool.isRequired,
    handleOnChange: PropTypes.func.isRequired,
    addItem: PropTypes.func.isRequired,
    removeItem: PropTypes.func.isRequired,
    item: PropTypes.object.isRequired,
    checkbox: PropTypes.bool,
    t: PropTypes.func.isRequired,
    dataTestId: PropTypes.string.isRequired,
    onDragStart: PropTypes.func,
    onDragOver: PropTypes.func,
    onDrop: PropTypes.func,
    readOnly: PropTypes.bool
};

export default withTranslation()(ChecklistItem);

